/**
 * X-Frame Companion App - Pharmacokinetic Calculations
 *
 * This module provides blood level curve calculations using the Bateman equation
 * for one-compartment pharmacokinetics with first-order absorption and elimination.
 *
 * The model properly accounts for:
 * - Absorption half-life (depot release rate for IM injections)
 * - Elimination half-life (terminal half-life)
 * - Ester ratio (fraction of active hormone by weight)
 * - Bioavailability
 * - Empirical scale factor for ng/dL conversion
 */

// ============================================
// INTERFACES
// ============================================

export interface PKParameters {
  halfLifeDays: number;           // Terminal elimination half-life
  absorptionHalfLifeDays: number; // Release rate from IM depot
  esterRatio: number;             // Fraction of active hormone by weight
  bioavailability: number;        // Fraction absorbed (0-1), default 1.0 for IM
  scaleFactor: number;            // Empirical conversion to ng/dL
}

export interface DoseEvent {
  day: number;        // Day of administration (can be fractional)
  doseMg: number;     // Dose in mg
}

export interface CurvePoint {
  time: number;          // Days since start (can be fractional)
  concentration: number; // ng/dL
}

export interface BloodLevelResult {
  day: number;
  level: number;      // Concentration in ng/dL
  isInjectionDay: boolean;
}

// ============================================
// CORE CALCULATION FUNCTIONS
// ============================================

/**
 * Calculate the elimination rate constant from half-life
 */
export function calculateEliminationConstant(halfLifeDays: number): number {
  return Math.LN2 / halfLifeDays;
}

/**
 * Calculate concentration at a specific time point using the Bateman equation.
 *
 * The Bateman equation models one-compartment pharmacokinetics with
 * first-order absorption and first-order elimination:
 *
 * C(t) = (F * D * ka) / (Vd * (ka - ke)) * (e^(-ke*t) - e^(-ka*t))
 *
 * Since we normalize Vd into the scaleFactor, the working formula becomes:
 * C(t) = D * esterRatio * bioavailability * scaleFactor * (ka / (ka - ke)) * (e^(-ke*t) - e^(-ka*t))
 */
export function calculateConcentrationAtTime(
  doseMg: number,
  params: PKParameters,
  timeSinceDoseDays: number
): number {
  // No concentration before injection
  if (timeSinceDoseDays <= 0) return 0;

  // Calculate rate constants from half-lives
  const ke = Math.LN2 / params.halfLifeDays;
  const ka = Math.LN2 / params.absorptionHalfLifeDays;

  // Apply ester ratio and bioavailability to get effective hormone dose
  const effectiveDose = doseMg * params.esterRatio * params.bioavailability;

  // Handle edge case where ka ≈ ke (prevents division by zero)
  // Use L'Hopital's result: C(t) = D * ka * t * e^(-ke*t)
  if (Math.abs(ka - ke) < 0.0001) {
    return effectiveDose * ka * timeSinceDoseDays *
           Math.exp(-ke * timeSinceDoseDays) * params.scaleFactor;
  }

  // Standard Bateman equation
  const coefficient = ka / (ka - ke);
  const concentration = effectiveDose * coefficient * (
    Math.exp(-ke * timeSinceDoseDays) - Math.exp(-ka * timeSinceDoseDays)
  );

  return Math.max(0, concentration * params.scaleFactor);
}

/**
 * Calculate total concentration at any time point by summing
 * contributions from all prior doses (superposition principle).
 */
export function calculateTotalConcentration(
  timePoint: number,
  doses: DoseEvent[],
  params: PKParameters
): number {
  let total = 0;

  for (const dose of doses) {
    const timeSinceDose = timePoint - dose.day;
    if (timeSinceDose > 0) {
      total += calculateConcentrationAtTime(dose.doseMg, params, timeSinceDose);
    }
  }

  return total;
}

/**
 * Generate a complete blood level curve with sub-day resolution.
 *
 * @param doses - Array of dose events
 * @param params - Pharmacokinetic parameters for the compound
 * @param totalDays - Total duration to calculate
 * @param pointsPerDay - Resolution (4 = every 6 hours, recommended minimum)
 */
export function generateConcentrationCurve(
  doses: DoseEvent[],
  params: PKParameters,
  totalDays: number,
  pointsPerDay: number = 4
): CurvePoint[] {
  const curve: CurvePoint[] = [];
  const timeStep = 1 / pointsPerDay;

  for (let t = 0; t <= totalDays; t += timeStep) {
    curve.push({
      time: t,
      concentration: calculateTotalConcentration(t, doses, params)
    });
  }

  return curve;
}

// ============================================
// DOSE SCHEDULE GENERATION
// ============================================

export type InjectionFrequency =
  | 'daily' | 'eod' | 'e3d' | '3x_week' | '2x_week' | '1x_week' | 'e10d' | 'e14d';

export function generateDoseSchedule(
  doseMg: number,
  frequency: InjectionFrequency | number,
  startDay: number,
  endDay: number
): DoseEvent[] {
  const doses: DoseEvent[] = [];

  let interval: number;

  if (typeof frequency === 'number') {
    interval = frequency;
  } else {
    const frequencyToDays: Record<InjectionFrequency, number> = {
      'daily': 1,
      'eod': 2,
      'e3d': 3,
      '3x_week': 7 / 3,  // ~2.33 days
      '2x_week': 3.5,
      '1x_week': 7,
      'e10d': 10,
      'e14d': 14
    };
    interval = frequencyToDays[frequency];
  }

  // Generate doses at fractional intervals (not rounded)
  for (let day = startDay; day < endDay; day += interval) {
    doses.push({ day, doseMg });
  }

  return doses;
}

// ============================================
// BLOOD LEVEL CALCULATION (Main API)
// ============================================

/**
 * Calculate blood levels using the Bateman equation model.
 * This is the main function for generating chart data.
 *
 * @param doses - Array of dose events
 * @param params - Pharmacokinetic parameters
 * @param totalDays - Total days to calculate
 * @param pointsPerDay - Resolution (default 4 = every 6 hours)
 */
export function calculateBloodLevels(
  doses: DoseEvent[],
  params: PKParameters,
  totalDays: number,
  pointsPerDay: number = 4
): BloodLevelResult[] {
  // Create a set of injection days for quick lookup (using floor for day matching)
  const injectionDays = new Set(doses.map(d => Math.floor(d.day)));

  const curve = generateConcentrationCurve(doses, params, totalDays, pointsPerDay);

  return curve.map(point => ({
    day: point.time,
    level: point.concentration,
    isInjectionDay: injectionDays.has(Math.floor(point.time))
  }));
}

/**
 * Calculate blood levels with daily resolution (legacy compatibility).
 * Returns one point per day for simpler charts.
 */
export function calculateBloodLevelsDaily(
  doses: DoseEvent[],
  params: PKParameters,
  totalDays: number
): BloodLevelResult[] {
  const injectionDays = new Set(doses.map(d => Math.floor(d.day)));
  const results: BloodLevelResult[] = [];

  for (let day = 0; day < totalDays; day++) {
    results.push({
      day,
      level: calculateTotalConcentration(day, doses, params),
      isInjectionDay: injectionDays.has(day)
    });
  }

  return results;
}

// ============================================
// STEADY STATE CALCULATIONS
// ============================================

/**
 * Calculate time to reach steady state (approximately 5 half-lives)
 */
export function calculateTimeToSteadyState(halfLifeDays: number): number {
  return halfLifeDays * 5;
}

/**
 * Calculate the accumulation ratio at steady state
 * R = 1 / (1 - e^(-k*tau))
 * Where tau is the dosing interval
 */
export function calculateAccumulationRatio(
  halfLifeDays: number,
  dosingIntervalDays: number
): number {
  const k = calculateEliminationConstant(halfLifeDays);
  return 1 / (1 - Math.exp(-k * dosingIntervalDays));
}

/**
 * Calculate peak and trough levels at steady state
 */
export interface SteadyStateLevels {
  peak: number;
  trough: number;
  average: number;
  fluctuation: number; // (peak - trough) / average
}

export function calculateSteadyStateLevels(
  doseMg: number,
  params: PKParameters,
  dosingIntervalDays: number
): SteadyStateLevels {
  // Generate enough doses to reach steady state
  const steadyStateTime = calculateTimeToSteadyState(params.halfLifeDays);
  const numDoses = Math.ceil((steadyStateTime + dosingIntervalDays * 2) / dosingIntervalDays);

  const doses: DoseEvent[] = [];
  for (let i = 0; i < numDoses; i++) {
    doses.push({ day: i * dosingIntervalDays, doseMg });
  }

  // Calculate levels at fine resolution in the last dosing interval
  const lastDoseDay = (numDoses - 1) * dosingIntervalDays;
  const endDay = lastDoseDay + dosingIntervalDays;

  let peak = 0;
  let trough = Infinity;
  let sum = 0;
  let count = 0;

  // Sample at high resolution
  const pointsPerDay = 24;
  for (let t = lastDoseDay; t <= endDay; t += 1/pointsPerDay) {
    const level = calculateTotalConcentration(t, doses, params);
    peak = Math.max(peak, level);
    trough = Math.min(trough, level);
    sum += level;
    count++;
  }

  const average = sum / count;
  const fluctuation = average > 0 ? (peak - trough) / average : 0;

  return { peak, trough, average, fluctuation };
}

// ============================================
// MULTIPLE COMPOUND HANDLING
// ============================================

export interface CompoundCurve {
  compoundName: string;
  levels: BloodLevelResult[];
  color?: string;
}

/**
 * Combine blood levels from multiple compounds.
 * Each compound generates its own independent curve (NOT summed).
 */
export function combineCompoundCurves(
  curves: CompoundCurve[]
): { day: number; total: number; byCompound: Record<string, number> }[] {
  if (curves.length === 0) return [];

  // Find the maximum length across all curves
  const maxLength = Math.max(...curves.map(c => c.levels.length));
  const combined: { day: number; total: number; byCompound: Record<string, number> }[] = [];

  for (let i = 0; i < maxLength; i++) {
    const byCompound: Record<string, number> = {};
    let total = 0;

    // Get the time point from the first curve that has this index
    let day = 0;
    for (const curve of curves) {
      if (curve.levels[i]) {
        day = curve.levels[i].day;
        break;
      }
    }

    for (const curve of curves) {
      const level = curve.levels[i]?.level ?? 0;
      byCompound[curve.compoundName] = level;
      total += level;
    }

    combined.push({ day, total, byCompound });
  }

  return combined;
}

// ============================================
// DEFAULT PK PARAMETERS
// ============================================

/**
 * Reference PK values for common compounds.
 * Use these when compound-specific data is not available.
 */
export const DEFAULT_PK_PARAMS: Record<string, PKParameters> = {
  'Testosterone Enanthate': {
    halfLifeDays: 4.5,
    absorptionHalfLifeDays: 1.5,
    esterRatio: 0.72,
    bioavailability: 1.0,
    scaleFactor: 8.5
  },
  'Testosterone Cypionate': {
    halfLifeDays: 5.0,
    absorptionHalfLifeDays: 1.5,
    esterRatio: 0.70,
    bioavailability: 1.0,
    scaleFactor: 8.5
  },
  'Testosterone Propionate': {
    halfLifeDays: 1.0,
    absorptionHalfLifeDays: 0.4,
    esterRatio: 0.84,
    bioavailability: 1.0,
    scaleFactor: 8.5
  },
  'Testosterone Phenylpropionate': {
    halfLifeDays: 2.5,
    absorptionHalfLifeDays: 0.8,
    esterRatio: 0.69,
    bioavailability: 1.0,
    scaleFactor: 8.5
  },
  'Testosterone Isocaproate': {
    halfLifeDays: 3.1,
    absorptionHalfLifeDays: 1.0,
    esterRatio: 0.75,
    bioavailability: 1.0,
    scaleFactor: 8.5
  },
  'Testosterone Decanoate': {
    halfLifeDays: 5.6,
    absorptionHalfLifeDays: 1.8,
    esterRatio: 0.65,
    bioavailability: 1.0,
    scaleFactor: 8.5
  },
  'Testosterone Undecanoate (Nebido)': {
    halfLifeDays: 20.0,
    absorptionHalfLifeDays: 5.0,
    esterRatio: 0.65,
    bioavailability: 1.0,
    scaleFactor: 8.5
  },
  'Testosterone Suspension': {
    halfLifeDays: 1.0,
    absorptionHalfLifeDays: 0.1,
    esterRatio: 1.0,
    bioavailability: 1.0,
    scaleFactor: 8.5
  },
  'Primobolan Enanthate': {
    halfLifeDays: 10.5,
    absorptionHalfLifeDays: 2.0,
    esterRatio: 0.70,
    bioavailability: 1.0,
    scaleFactor: 12.0
  },
  'Methenolone Enanthate': {
    halfLifeDays: 10.5,
    absorptionHalfLifeDays: 2.0,
    esterRatio: 0.70,
    bioavailability: 1.0,
    scaleFactor: 12.0
  },
  'Trenbolone Acetate': {
    halfLifeDays: 1.5,
    absorptionHalfLifeDays: 0.5,
    esterRatio: 0.87,
    bioavailability: 1.0,
    scaleFactor: 15.0
  },
  'Trenbolone Enanthate': {
    halfLifeDays: 7.0,
    absorptionHalfLifeDays: 2.0,
    esterRatio: 0.71,
    bioavailability: 1.0,
    scaleFactor: 15.0
  },
  'Trenbolone Hexahydrobenzylcarbonate': {
    halfLifeDays: 14.0,
    absorptionHalfLifeDays: 3.0,
    esterRatio: 0.65,
    bioavailability: 1.0,
    scaleFactor: 15.0
  },
  'Nandrolone Decanoate': {
    halfLifeDays: 7.0,
    absorptionHalfLifeDays: 2.5,
    esterRatio: 0.64,
    bioavailability: 1.0,
    scaleFactor: 10.0
  },
  'Nandrolone Phenylpropionate': {
    halfLifeDays: 2.5,
    absorptionHalfLifeDays: 0.8,
    esterRatio: 0.67,
    bioavailability: 1.0,
    scaleFactor: 10.0
  },
  'Boldenone Undecylenate': {
    halfLifeDays: 14.0,
    absorptionHalfLifeDays: 3.5,
    esterRatio: 0.61,
    bioavailability: 1.0,
    scaleFactor: 11.0
  },
  'Drostanolone Propionate': {
    halfLifeDays: 2.0,
    absorptionHalfLifeDays: 0.5,
    esterRatio: 0.84,
    bioavailability: 1.0,
    scaleFactor: 10.0
  },
  'Drostanolone Enanthate': {
    halfLifeDays: 4.5,
    absorptionHalfLifeDays: 1.5,
    esterRatio: 0.73,
    bioavailability: 1.0,
    scaleFactor: 10.0
  }
};

/**
 * Get PK parameters for a compound, with fallback to estimated values.
 * Uses compound data if available, otherwise estimates from half-life.
 */
export function getPKParameters(
  compoundName: string,
  halfLifeDays: number,
  esterRatio?: number,
  bioavailability?: number
): PKParameters {
  // Check for known compound parameters
  if (DEFAULT_PK_PARAMS[compoundName]) {
    return DEFAULT_PK_PARAMS[compoundName];
  }

  // Estimate absorption half-life from elimination half-life
  // Rule of thumb: absorption is typically 1/3 to 1/4 of elimination for IM depots
  const absorptionHalfLifeDays = halfLifeDays * 0.33;

  // Default ester ratio estimate based on typical ester weights
  const estimatedEsterRatio = esterRatio ?? 0.72;

  // Default bioavailability for IM injections
  const estimatedBioavailability = bioavailability ?? 1.0;

  // Default scale factor (can be calibrated per compound)
  const scaleFactor = 8.5;

  return {
    halfLifeDays,
    absorptionHalfLifeDays,
    esterRatio: estimatedEsterRatio,
    bioavailability: estimatedBioavailability,
    scaleFactor
  };
}

// ============================================
// LEGACY COMPATIBILITY
// ============================================

/**
 * @deprecated Use calculateBloodLevels with PKParameters instead.
 * This interface is kept for backwards compatibility.
 */
export interface LegacyPKParameters {
  halfLifeDays: number;
  cmax?: number | null;
  tmaxDays?: number | null;
  bioavailability?: number | null;
  pkModel: 'basic' | 'advanced';
}

/**
 * Convert legacy parameters to new PKParameters format.
 */
export function convertLegacyParams(
  legacy: LegacyPKParameters,
  compoundName?: string
): PKParameters {
  // If we have a known compound, use its parameters
  if (compoundName && DEFAULT_PK_PARAMS[compoundName]) {
    return DEFAULT_PK_PARAMS[compoundName];
  }

  // Estimate from legacy parameters
  const absorptionHalfLifeDays = legacy.tmaxDays
    ? legacy.tmaxDays * 0.7  // Approximate: tmax ≈ 1.4 * absorption half-life
    : legacy.halfLifeDays * 0.33;

  return {
    halfLifeDays: legacy.halfLifeDays,
    absorptionHalfLifeDays,
    esterRatio: legacy.bioavailability ?? 0.72,
    bioavailability: 1.0,  // Assume full bioavailability for IM
    scaleFactor: legacy.cmax ?? 8.5
  };
}

export default {
  calculateBloodLevels,
  calculateBloodLevelsDaily,
  generateDoseSchedule,
  calculateTimeToSteadyState,
  calculateAccumulationRatio,
  calculateSteadyStateLevels,
  combineCompoundCurves,
  getPKParameters,
  convertLegacyParams,
  DEFAULT_PK_PARAMS
};
